/**
 * Der Bericht als Vorschlag: Layout S3 „Beweiskette“ (seit 09.10.2026, Robert
 * im Canvas „99takes Report-Template“, Entwürfe S3-Desktop und S3-Mobil).
 *
 * Erst der Vorschlag, dann die Belege: Seitenkopf mit Sprunglinks, ein ruhiger
 * Hero (Kicker, Überschrift, ein Satz, zwei Knöpfe, rechts die neue Seite),
 * „Vorher und nachher“, die drei Belege als Karten, dann jeder Beleg
 * ausführlich (Text-Simulation, Design-Simulation, Fachprüfer), zuletzt die
 * Akkordeons aus Layout A und der Fußhinweis. Kein Abschnitt mit To-dos: die
 * Empfehlungen stehen nur im Akkordeon „Alle Empfehlungen“ (Robert, 09.10.2026).
 *
 * Die Rechnung steht in `bericht-vorschlag.ts`. Akkordeons und Fuß kommen aus
 * `Bericht.tsx` (BerichtBlatt), damit nichts aus Layout A verloren geht.
 */

import { useState } from 'react';
import { leseBilanz, bilanzSatz, leseKernRand, leseMuster, musterSatz, randWarnung } from '../kern-rand';
import { kollisionenDesBerichts, leitplankenTitel } from '../leitplanken-liste';
import { kernRandZahl, standDer } from '../bericht-blatt';
import { kurzname } from '../bericht-auswertung';
import { kernRandText } from '../bericht-ablauf';
import { locale, sprache, t } from '../sprache';
import {
  artDerNeuen, belegeDes, belegName, BELEG_ANKER, fachprueferBeleg, keinerDagegen, kriteriumKurz, markerText, mitVorherNachher, noteBei,
  SKIZZE_SATZ, SKIZZE_SATZ_EN, sprungziele, stimmeHeute, stimmenNeu, stoertNoch, vergleichFuerSatz, vergleichsBilder, vergleichsChecks, vorzeichen, wasWirkt, zahl1,
  type BelegArt, type Belege, type DesignLesart, type FachprueferBeleg, type Seitenbilder, type Sprungziel, type TextBeleg, type Vorschlag,
} from '../bericht-vorschlag';

const SERIF = "font-serif";
const KOPF = `${SERIF} font-semibold tracking-[-0.01em] text-[#1d1f26]`;
const KICK_FORM = 'text-[0.65625rem] font-semibold tracking-[0.12em] uppercase md:text-[0.71875rem]';
const KICK = `${KICK_FORM} text-[#5f646b]`;
const FOKUS = 'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[#1f6964]';
const ZAHLWORT = ['null', 'eine', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun', 'zehn', 'elf', 'zwölf'];
const ZAHLWORT_EN = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
const wort = (n: number) => t(ZAHLWORT, ZAHLWORT_EN)[n] ?? String(n);
const Wort = (n: number) => { const w = wort(n); return w.charAt(0).toUpperCase() + w.slice(1); };

const tag = (iso: unknown): string => {
  const d = new Date(String(iso ?? ''));
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Ein Zitat in deutschen (englisch: englischen) Anführungszeichen; Anführungszeichen darin werden einfache. */
const zitiert = (x: unknown) => sprache() === 'en'
  ? `“${String(x ?? '').trim().replace(/[„“]([^„“”"]*)[“”"]/g, '‘$1’')}”`
  : `„${String(x ?? '').trim().replace(/[„“]([^„“”"]*)[“”"]/g, '‚$1‘')}“`;

export function BerichtVorschlag({ data, v, datum, details, fuss }: {
  data: any; v: Vorschlag; datum: string; details: React.ReactNode; fuss: React.ReactNode;
}) {
  const bilder = vergleichsBilder(data);
  const belege = belegeDes(data);
  const vn = mitVorherNachher(v, bilder);
  const ziele = sprungziele(vn, belege);
  const namen: string[] = (Array.isArray(data?.set?.personas) ? data.set.personas : []).map((p: any) => String(p?.name ?? '')).filter(Boolean);
  const kurz = (label: string) => kurzname(label, namen);
  const skizze = artDerNeuen(data) === 'skizze';

  return (
    <div className="flex flex-col gap-12 md:gap-[72px] print:gap-10">
      <div className="flex flex-col gap-[22px] md:gap-[72px] print:gap-8">
        <div className="flex flex-col gap-[18px]">
          <header className="flex flex-col gap-1 border-b border-[#dde3e1] pb-3 md:flex-row md:items-center md:justify-between md:gap-6 md:pb-4">
            <span className={KICK}>{t('Zielgruppen-Simulation', 'Audience simulation')} · {data.study?.name}</span>
            {/* Den Sprachumschalter trägt die Zeile mit dem Logo darüber (Papier in Bericht.tsx), auch für S3. */}
            <span className="text-[0.75rem] leading-snug text-[#5f646b] md:text-[0.8125rem]">{t(`Stand ${datum} · mit 99takes`, `As of ${datum} · with 99takes`)}</span>
          </header>
          <Sprunglinks ziele={ziele} />
        </div>
        <Hero v={v} bilder={bilder.neu} desktopUnten={vn && !!bilder.neu?.desktop} skizze={skizze} />
      </div>
      <Sprunglinks ziele={ziele} mobil />

      {vn && <VorherNachher v={v} bilder={bilder} data={data} />}
      {belege.reihe.length > 0 && <BelegeUebersicht belege={belege} data={data} />}
      {belege.text && <BelegText t={belege.text} nr={nrVon(belege, 'text')} von={belege.reihe.length} data={data} kurz={kurz} />}
      {belege.design && <BelegDesign d={belege.design} nr={nrVon(belege, 'design')} von={belege.reihe.length} skizze={skizze} />}
      {belege.fachpruefer && (
        <BelegFachpruefer f={belege.fachpruefer} nr={nrVon(belege, 'fachpruefer')} von={belege.reihe.length} skizze={skizze}
          heute={vergleichsChecks(data).heute ? fachprueferBeleg(vergleichsChecks(data).heute) : null} />
      )}

      <div id="details" className="scroll-mt-6">{details}</div>
      {fuss}
    </div>
  );
}

const nrVon = (b: Belege, art: BelegArt) => b.reihe.find((x) => x.art === art)?.nr ?? 0;

// ─── Kopf ────────────────────────────────────────────────────────────────────

/** Sprunglinks „Auf dieser Seite“: am Desktop unter dem Seitenkopf, am Handy nach dem Hero. Echte Anker, mindestens 44 px hoch. */
function Sprunglinks({ ziele, mobil = false }: { ziele: Sprungziel[]; mobil?: boolean }) {
  return (
    <nav aria-label={t('Auf dieser Seite', 'On this page')}
      className={mobil
        ? 'flex flex-wrap items-center gap-1.5 rounded-[14px] bg-[#eef2f1] p-4 md:hidden print:hidden'
        : 'hidden flex-wrap items-center gap-2 md:flex print:hidden'}>
      <span className={`${KICK} mr-1.5`}>{t('Auf dieser Seite', 'On this page')}</span>
      {ziele.map((z) => (
        <a key={z.id} href={`#${z.id}`}
          className={`inline-flex min-h-11 items-center rounded-full border border-[#d9e0de] bg-[#ffffff] px-3 text-[0.8125rem] font-medium text-[#1d1f26] no-underline hover:border-[#3f8a87] hover:text-[#1f6964] md:px-[15px] md:text-[0.84375rem] ${FOKUS}`}>
          {z.label}
        </a>
      ))}
    </nav>
  );
}

/** Der Pfeil auf einem Knopf, der in einem neuen Tab öffnet. */
function NeuerTab({ farbe }: { farbe: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={farbe} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

/**
 * Der Hero bleibt ruhig (Robert, 09.10.2026): Kicker, Überschrift, ein Satz,
 * zwei Knöpfe, rechts die neue Seite am Desktop mit überlappendem Handy. Am
 * Handy nur das Handy, zwischen Text und Knöpfen.
 */
function Hero({ v, bilder, desktopUnten, skizze }: { v: Vorschlag; bilder: Seitenbilder | null; desktopUnten: boolean; skizze: boolean }) {
  const desktop = bilder?.desktop ?? null;
  const mobil = bilder?.mobil ?? null;
  const mitBild = !!(desktop || mobil);
  const beide = !!(desktop && mobil);
  return (
    <section aria-labelledby="vorschlag-titel"
      className={`grid gap-[18px] break-inside-avoid ${mitBild ? 'lg:grid-cols-[430px_minmax(0,1fr)] lg:items-center lg:gap-x-14 lg:gap-y-6' : ''}`}>
      <div className={`flex flex-col gap-2.5 md:gap-3.5 ${mitBild ? 'lg:col-start-1 lg:row-start-1 lg:self-end' : ''}`}>
        <span className={`${KICK_FORM} text-[#1f6964]`}>{t('Unser Vorschlag', 'Our proposal')}</span>
        <h1 id="vorschlag-titel" className={`${KOPF} m-0 max-w-[54rem] text-[1.9375rem] leading-[1.1] md:text-[3rem] md:leading-[1.06] [text-wrap:balance]`}>{v.titel}</h1>
        {v.text && <p className="m-0 max-w-[62ch] text-[0.90625rem] leading-relaxed text-[#2f3238] md:text-[1.0625rem]">{v.text}</p>}
        {/* Eine Skizze ist keine Gestaltung, und das steht überall, wo sie gezeigt wird (AGENTS.md, „Skizze“). */}
        {skizze && mitBild && <SkizzeZeile />}
      </div>

      {mitBild && (
        <figure className={`m-0 flex flex-col items-center gap-2.5 ${beide ? 'sm:relative sm:block sm:aspect-[666/470]' : ''} lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center print:relative print:block print:aspect-[666/470]`}>
          {desktop && (
            <img src={desktop} alt={t('Erster Bildschirm der neuen Fassung am Desktop', 'First screen of the new version on desktop')}
              className={`${mobil ? 'hidden sm:block sm:absolute sm:top-0 sm:left-0 sm:w-[91%] print:absolute print:block print:w-[91%]' : 'block w-full'} aspect-[1280/800] rounded-xl border border-[#d9e0de] bg-white object-cover object-top shadow-[0_18px_40px_-24px_rgba(28,24,21,.35)]`} />
          )}
          {mobil && (
            <div className={`w-[230px] rounded-[24px] bg-[#1d1f26] p-[5px] shadow-[0_18px_34px_-18px_rgba(28,24,21,.55)] ${desktop ? 'sm:absolute sm:right-0 sm:bottom-0 sm:w-[25%] sm:rounded-[26px] sm:p-1.5 print:absolute print:right-0 print:bottom-0 print:w-[25%]' : ''}`}>
              <img src={mobil} alt={t('Erster Bildschirm der neuen Fassung am Handy', 'First screen of the new version on mobile')} className="block aspect-[340/720] w-full rounded-[19px] bg-white object-cover object-top sm:rounded-[20px]" />
            </div>
          )}
          {mobil && (
            <figcaption className={`text-center text-[0.75rem] leading-snug text-[#565b62] ${desktop ? 'sm:hidden print:hidden' : ''}`}>
              {t('Neue Fassung am Handy.', 'New version on mobile.')}{desktop && desktopUnten ? t(' Den Desktop seht ihr unten unter „Vorher und nachher“.', ' You can see desktop below under “Before and after”.') : ''}
            </figcaption>
          )}
        </figure>
      )}

      {(v.entwurf_url || v.texte_url) && (
        <div className={`flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 ${mitBild ? 'lg:col-start-1 lg:row-start-2 lg:self-start' : ''}`}>
          {v.entwurf_url && (
            <a href={v.entwurf_url} target="_blank" rel="noopener noreferrer"
              className={`inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full bg-[#1d1f26] px-[22px] text-[0.9375rem] font-semibold text-[#f9fbfa] no-underline hover:bg-[#2f3238] ${FOKUS}`}>
              {t('Entwurf ansehen, klickbar', 'View clickable draft')}<NeuerTab farbe="#f9fbfa" /><span className="sr-only">{t(' (neuer Tab)', ' (new tab)')}</span>
            </a>
          )}
          {v.texte_url && (
            <a href={v.texte_url} target="_blank" rel="noopener noreferrer"
              className={`inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full border-[1.5px] border-[#c3cbc9] bg-[#ffffff] px-[22px] text-[0.9375rem] font-semibold text-[#1d1f26] no-underline hover:border-[#1f6964] ${FOKUS}`}>
              {t('Alle Texte', 'All copy')}<span className="sr-only">{t(' (neuer Tab)', ' (new tab)')}</span>
            </a>
          )}
          {/* Im PDF sind Knöpfe tot: die Adressen stehen darunter. */}
          <p className="m-0 hidden text-[8.5pt] leading-snug text-[#565b62] [overflow-wrap:anywhere] print:block">
            {v.entwurf_url && <>{t('Entwurf: ', 'Draft: ')}{v.entwurf_url}<br /></>}
            {v.texte_url && <>{t('Alle Texte: ', 'All copy: ')}{v.texte_url}</>}
          </p>
        </div>
      )}
    </section>
  );
}

/** Der Hinweis an jeder Stelle, die eine Skizze zeigt: neuer Text in der heutigen Gestaltung, kein Design. */
function SkizzeZeile({ pruefer = false }: { pruefer?: boolean }) {
  return (
    <p className="m-0 max-w-[62ch] rounded-lg border border-[#dde3e1] bg-[#eef2f1] px-3.5 py-2.5 text-[0.8125rem] leading-relaxed text-[#2f3238] md:text-[0.875rem]">
      {t(SKIZZE_SATZ, SKIZZE_SATZ_EN)}
      {pruefer ? t(' Abstände und fehlende Titel, die der Fachprüfer findet, können vom Einsetzen des Texts kommen.', ' Spacing issues and missing headings the expert reviewer finds may come from inserting the copy.') : ''}
    </p>
  );
}

// ─── Vorher und nachher ──────────────────────────────────────────────────────

function Pille({ neu, children }: { neu?: boolean; children: React.ReactNode }) {
  return (
    <span className={`self-start justify-self-start rounded-full px-[11px] py-1 text-[0.75rem] font-semibold md:text-[0.78125rem] ${neu ? 'bg-[#f6e3d2] text-[#8a4a17]' : 'bg-[#e8eceb] text-[#2f3238]'}`}>
      {children}
    </span>
  );
}

function Bild({ src, alt, art }: { src: string; alt: string; art: 'desktop' | 'mobil' }) {
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" className={`block min-w-0 ${FOKUS} rounded-[10px]`} title={t('Ganze Aufnahme öffnen', 'Open full capture')}>
      <img src={src} alt={alt}
        className={`block w-full rounded-[10px] border border-[#d9e0de] bg-white object-cover object-top ${art === 'desktop' ? 'aspect-[1280/800]' : 'aspect-[340/720]'}`} />
      <span className="sr-only">{t(' (ganze Aufnahme in neuem Tab)', ' (full capture in new tab)')}</span>
    </a>
  );
}

function VorherNachher({ v, bilder, data }: { v: Vorschlag; bilder: { heute: Seitenbilder | null; neu: Seitenbilder | null }; data: any }) {
  const [ansicht, setAnsicht] = useState<'desktop' | 'mobil'>('desktop');
  const [offen, setOffen] = useState(false);
  const heute = bilder.heute;
  const neu = bilder.neu;
  const noteHandy = (rolle: 'heute' | 'neu') => {
    const n = data?.design_vergleich?.[rolle]?.noten?.handy;
    return typeof n === 'number' && Number.isFinite(n) ? n : null;
  };
  const spalten = heute ? ['heute', 'neu'] as const : ['neu'] as const;
  const quelle = { heute, neu };
  const mitDesktop = spalten.some((s) => quelle[s]?.desktop);
  const mitMobil = spalten.some((s) => quelle[s]?.mobil);
  const mitBildern = mitDesktop || mitMobil;
  const raster = heute ? 'md:grid-cols-[130px_minmax(0,1fr)_minmax(0,1fr)] print:grid-cols-[72px_minmax(0,1fr)_minmax(0,1fr)]'
    : 'md:grid-cols-[130px_minmax(0,1fr)] print:grid-cols-[72px_minmax(0,1fr)]';
  const mitHeuteText = v.aenderungen.some((a) => a.heute);
  const tabelle = mitHeuteText || heute
    ? 'md:grid-cols-[130px_minmax(0,1fr)_minmax(0,1fr)] print:grid-cols-[72px_minmax(0,1fr)_minmax(0,1fr)]'
    : 'md:grid-cols-[130px_minmax(0,1fr)] print:grid-cols-[72px_minmax(0,1fr)]';
  const titelBild = (rolle: 'heute' | 'neu', art: 'desktop' | 'mobil') => t(
    `${rolle === 'heute' ? 'Heutige Seite' : 'Neue Fassung'} ${art === 'desktop' ? 'am Desktop' : 'am Handy'}, erster Bildschirm`,
    `${rolle === 'heute' ? 'Current page' : 'New version'} ${art === 'desktop' ? 'on desktop' : 'on mobile'}, first screen`,
  );
  const pille = (s: 'heute' | 'neu') => (s === 'heute' ? t('Heute', 'Current') : t('Neu', 'New'));

  const handyZelle = (rolle: 'heute' | 'neu') => {
    const src = quelle[rolle]?.mobil;
    const note = noteHandy(rolle);
    if (!src) return <span className="text-[0.8125rem] text-[#5f646b]">{t('kein Bild', 'no image')}</span>;
    return (
      <div className="grid grid-cols-[150px_minmax(0,1fr)] items-end gap-5 print:grid-cols-[34mm_minmax(0,1fr)]">
        <Bild src={src} alt={titelBild(rolle, 'mobil')} art="mobil" />
        {note != null && (
          <span className="flex flex-col gap-0.5 pb-1">
            <span className="text-[0.8125rem] text-[#565b62]">{t('Note am Handy', 'Score on mobile')}</span>
            <b className={`${SERIF} text-[1.375rem] leading-none font-semibold ${rolle === 'neu' ? 'text-[#2f5a34]' : 'text-[#565b62]'}`}>{zahl1(note)}</b>
          </span>
        )}
      </div>
    );
  };

  return (
    <section id="vorher-nachher" aria-labelledby="vorher-nachher-titel" className="flex scroll-mt-6 flex-col gap-3.5 md:gap-[22px]">
      <div className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-1 border-b border-[#dde3e1] pb-2 break-inside-avoid break-after-avoid md:pb-3">
        <h2 id="vorher-nachher-titel" className={`${KOPF} m-0 text-[1.5625rem] leading-tight md:text-[2rem]`}>{t('Vorher und nachher', 'Before and after')}</h2>
        {mitBildern && <span className="hidden text-[0.8125rem] text-[#5f646b] md:inline">{t('Erster Bildschirm am Desktop und am Handy', 'First screen on desktop and mobile')}</span>}
      </div>

      {/* Ab 768 px und im Druck: heute und neu nebeneinander, Desktop über Handy. */}
      {mitBildern && (
        <div className={`hidden items-start gap-x-10 gap-y-[18px] md:grid print:grid print:gap-x-5 ${raster}`}>
          <span />
          {spalten.map((s) => <Pille key={s} neu={s === 'neu'}>{pille(s)}</Pille>)}
          {mitDesktop && (
            <>
              <span className={`${KICK} pt-1.5`}>Desktop</span>
              {spalten.map((s) => quelle[s]?.desktop
                ? <Bild key={s} src={quelle[s]!.desktop!} alt={titelBild(s, 'desktop')} art="desktop" />
                : <span key={s} className="text-[0.8125rem] text-[#5f646b]">{t('kein Bild', 'no image')}</span>)}
            </>
          )}
          {mitMobil && (
            <>
              <span className={`${KICK} pt-1.5`}>{t('Handy', 'Mobile')}</span>
              {spalten.map((s) => <div key={s}>{handyZelle(s)}</div>)}
            </>
          )}
        </div>
      )}

      {/* Am Handy: ein Umschalter zwischen Desktop und Handy. */}
      {mitBildern && (
        <div className="flex flex-col gap-3 md:hidden print:hidden">
          {mitDesktop && mitMobil && (
            <div role="group" aria-label={t('Ansicht wählen', 'Choose view')} className="flex gap-1 rounded-full bg-[#e8eceb] p-1">
              {(['desktop', 'mobil'] as const).map((a) => (
                <button key={a} type="button" aria-pressed={ansicht === a} onClick={() => setAnsicht(a)}
                  className={`min-h-11 flex-1 cursor-pointer rounded-full border-0 text-[0.875rem] font-semibold ${FOKUS} ${ansicht === a ? 'bg-[#ffffff] text-[#1d1f26] shadow-[0_1px_2px_rgb(20_18_12/0.1)]' : 'bg-transparent text-[#565b62]'}`}>
                  {a === 'desktop' ? 'Desktop' : t('Handy', 'Mobile')}
                </button>
              ))}
            </div>
          )}
          {(mitDesktop && (ansicht === 'desktop' || !mitMobil)) ? (
            <div className="flex flex-col gap-3">
              {spalten.filter((s) => quelle[s]?.desktop).map((s) => (
                <figure key={s} className="m-0 flex flex-col gap-1.5">
                  <Pille neu={s === 'neu'}>{pille(s)}</Pille>
                  <Bild src={quelle[s]!.desktop!} alt={titelBild(s, 'desktop')} art="desktop" />
                </figure>
              ))}
            </div>
          ) : (
            <div className={`grid gap-3 ${spalten.length === 2 ? 'grid-cols-2' : 'grid-cols-[minmax(0,180px)]'}`}>
              {spalten.filter((s) => quelle[s]?.mobil).map((s) => (
                <figure key={s} className="m-0 flex min-w-0 flex-col gap-1.5">
                  <span className="flex items-baseline justify-between gap-1.5">
                    <Pille neu={s === 'neu'}>{pille(s)}</Pille>
                    {noteHandy(s) != null && (
                      <span className="flex items-baseline gap-1">
                        <span className="text-[0.6875rem] text-[#5f646b]">{t('Note', 'Score')}</span>
                        <b className={`${SERIF} text-[1.125rem] font-semibold ${s === 'neu' ? 'text-[#2f5a34]' : 'text-[#565b62]'}`}><span className="sr-only">{t('am Handy ', 'on mobile ')}</span>{zahl1(noteHandy(s)!)}</b>
                      </span>
                    )}
                  </span>
                  <Bild src={quelle[s]!.mobil!} alt={titelBild(s, 'mobil')} art="mobil" />
                </figure>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Die Änderungen: ab 768 px als Zeilen, am Handy zum Aufklappen. */}
      {v.aenderungen.length > 0 && (
        <>
          <div className="hidden flex-col md:flex print:flex">
            {!mitBildern && (
              <div className={`grid gap-10 pb-2 print:gap-5 ${tabelle}`}>
                <span />
                {(mitHeuteText || heute) && <Pille>{pille('heute')}</Pille>}
                <Pille neu>{pille('neu')}</Pille>
              </div>
            )}
            {v.aenderungen.map((a, i) => (
              <div key={i} className={`grid items-baseline gap-10 border-t border-[#dde3e1] py-3.5 break-inside-avoid print:gap-5 print:py-2.5 ${tabelle}`}>
                <span className={KICK}>{a.bereich}</span>
                {(mitHeuteText || heute) && <span className="text-[0.875rem] leading-relaxed text-[#565b62]">{a.heute || '–'}</span>}
                <span className="text-[0.90625rem] leading-relaxed text-[#1d1f26]">
                  {a.neu}
                  {a.kollidiert_mit && <span className="mt-1 block text-[0.8125rem] text-[#1f6964]">{t('Kollidiert mit Leitplanke: ', 'Conflicts with guardrail: ')}{a.kollidiert_mit}</span>}
                </span>
              </div>
            ))}
          </div>
          <div className="flex flex-col border-b border-[#dde3e1] md:hidden print:hidden">
            <button type="button" aria-expanded={offen} aria-controls="aenderungen-liste" onClick={() => setOffen((o) => !o)}
              className={`flex min-h-[52px] w-full cursor-pointer items-center justify-between gap-3 border-0 border-t border-[#dde3e1] bg-transparent px-0 py-3 text-left text-[0.9375rem] font-semibold text-[#1d1f26] ${FOKUS}`}>
              <span className="flex min-w-0 flex-col gap-0.5">
                {t('Was sich im Einzelnen ändert', 'What changes in detail')}
                <span className="text-[0.78125rem] font-normal text-[#5f646b]">{v.aenderungen.map((a) => a.bereich).join(', ')}</span>
              </span>
              <span aria-hidden className="shrink-0 text-[1.25rem] font-normal text-[#5f646b]">{offen ? '−' : '+'}</span>
            </button>
            <div id="aenderungen-liste" className={offen ? 'flex flex-col' : 'hidden'}>
              {v.aenderungen.map((a, i) => (
                <div key={i} className="flex flex-col gap-1.5 border-t border-[#dde3e1] py-3">
                  <span className={KICK}>{a.bereich}</span>
                  {a.heute && <span className="text-[0.8125rem] leading-normal text-[#565b62]"><b className="font-semibold">{t('Heute:', 'Current:')}</b> {a.heute}</span>}
                  <span className="text-[0.84375rem] leading-normal text-[#1d1f26]"><b className="font-semibold text-[#8a4a17]">{t('Neu:', 'New:')}</b> {a.neu}</span>
                  {a.kollidiert_mit && <span className="text-[0.78125rem] text-[#1f6964]">{t('Kollidiert mit Leitplanke: ', 'Conflicts with guardrail: ')}{a.kollidiert_mit}</span>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

// ─── Die Belege ──────────────────────────────────────────────────────────────

/** Das Siegel eines Belegs: Nummer im Doppelring, ein Haken, wenn der Beleg eindeutig für die neue Fassung spricht. */
function Siegel({ nr, haken, klein = false }: { nr: number; haken: boolean; klein?: boolean }) {
  return (
    <span aria-hidden
      className={`relative flex shrink-0 items-center justify-center rounded-full border-[1.5px] border-[#2f5a34] bg-[#e4ede3] ${klein
        ? 'size-10 shadow-[inset_0_0_0_3px_#e4ede3,inset_0_0_0_4.5px_#2f5a34] md:size-[52px] md:shadow-[inset_0_0_0_4px_#e4ede3,inset_0_0_0_5.5px_#2f5a34]'
        : 'size-12 shadow-[inset_0_0_0_4px_#e4ede3,inset_0_0_0_5.5px_#2f5a34] md:size-[52px]'}`}>
      <span className={`${SERIF} font-semibold leading-none text-[#2f5a34] ${klein ? 'text-[1.125rem] md:text-[1.375rem]' : 'text-[1.3125rem] md:text-[1.375rem]'}`}>{nr}</span>
      {haken && (
        <span className="absolute -right-[3px] -bottom-[3px] flex size-[21px] items-center justify-center rounded-full border-2 border-[#ffffff] bg-[#2f5a34]">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
        </span>
      )}
    </span>
  );
}

function Balkenpaar({ heute, neu, altLabel, dick = false }: { heute: number | null; neu: number | null; altLabel: string; dick?: boolean }) {
  // „Vergleich“ ist länger als „heute“: dann eine breitere Spalte für beide Zeilen.
  const breite = altLabel.length > 5 ? 'w-[4.25rem]' : 'w-10 md:w-11';
  const zeile = (label: string, wert: number | null, farbe: string) => (
    <div className="flex items-center gap-2 md:gap-2.5">
      <span className={`${breite} shrink-0 text-[#5f646b]`}>{label}</span>
      <span className={`min-w-0 flex-1 rounded-md bg-[#e8eceb] ${dick ? 'h-2.5 md:h-3' : 'h-2'}`} aria-hidden>
        {wert != null && <span className="block h-full rounded-md" style={{ width: `${Math.min(100, Math.max(0, wert * 10))}%`, background: farbe }} />}
      </span>
      <b className="w-7 shrink-0 text-right font-semibold tabular-nums">{wert != null ? zahl1(wert) : '–'}</b>
    </div>
  );
  return (
    <div className={`flex flex-col ${dick ? 'gap-2 text-[0.8125rem] md:gap-[9px] md:text-[0.84375rem]' : 'gap-[5px] text-[0.78125rem]'}`}>
      {zeile(altLabel, heute, '#aeb7b5')}
      {zeile(t('neu', 'new'), neu, '#3f8a87')}
    </div>
  );
}

function ZumBeleg({ nr }: { nr: number }) {
  return (
    <span className="mt-auto inline-flex min-h-8 items-center gap-[5px] text-[0.84375rem] font-semibold text-[#1f6964] group-hover:underline print:hidden">
      {t(`Zum Beleg ${nr}`, `To evidence ${nr}`)}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
    </span>
  );
}

function BelegeUebersicht({ belege, data }: { belege: Belege; data: any }) {
  const n = belege.reihe.length;
  // „Was für diese Fassung spricht“, wenn kein Beleg dagegen spricht (wie im
  // Entwurf). Nicht „alle bestanden“: „am Handy deutlich besser, sonst etwa
  // gleichauf“ besteht nicht, spricht aber auch nicht dagegen.
  const dafuer = keinerDagegen(belege);
  const personas: any[] = Array.isArray(data?.set?.personas) ? data.set.personas : [];
  const recherchiert = personas.some((p) => Array.isArray(p?.evidence) && p.evidence.length > 0);
  const zielgruppe = typeof data?.set?.name === 'string' && data.set.name.trim()
    ? t(`aus der Zielgruppe „${data.set.name.trim()}“`, `from the audience “${data.set.name.trim()}”`)
    : t('aus der Zielgruppe', 'from the audience');
  const karte = `group flex min-w-0 flex-col gap-3 rounded-[14px] border border-[#dde3e1] bg-[#ffffff] p-4 text-[#1d1f26] no-underline break-inside-avoid hover:border-[#3f8a87] md:gap-3.5 md:rounded-2xl md:p-[22px] ${FOKUS}`;
  const kopf = (nr: number, art: BelegArt, haken: boolean) => (
    <span className="flex items-center gap-3.5">
      <Siegel nr={nr} haken={haken} />
      <span className={KICK}>{t('Beleg', 'Evidence')} {nr} · {art === 'fachpruefer' ? t('Fachprüfer (KI)', 'Expert reviewer (AI)') : belegName(art)}</span>
    </span>
  );
  const gross = `${SERIF} text-[1.75rem] leading-none font-semibold md:text-[2.125rem]`;
  return (
    <section id="belege" aria-labelledby="belege-titel" className="flex scroll-mt-6 flex-col gap-4 md:gap-[22px]">
      <div className="flex flex-col gap-2 border-b border-[#dde3e1] pb-3 break-inside-avoid break-after-avoid">
        <span className={KICK}>{t('Die Belege', 'The evidence')}</span>
        <h2 id="belege-titel" className={`${KOPF} m-0 text-[1.4375rem] leading-[1.15] md:text-[2rem]`}>{dafuer ? t('Was für diese Fassung spricht', 'What speaks for this version') : t('Was die Prüfungen zeigen', 'What the checks show')}</h2>
        <p className="m-0 max-w-[760px] text-[0.8125rem] leading-relaxed text-[#565b62] md:text-[0.9375rem]">
          {t(<>
          {Wort(n)} {n === 1 ? 'Prüfung' : 'Prüfungen'} mit dem Simulationswerkzeug 99takes. Alle simuliert:{' '}
          {recherchiert ? `recherchierte Personas ${zielgruppe}` : `Personas ${zielgruppe}, ohne Belege aus echten Quellen`}
          {belege.fachpruefer ? ' und ein KI-Prüfer' : ''}, keine echten Besucher.
          </>, <>
          {Wort(n)} {n === 1 ? 'check' : 'checks'} with the simulation tool 99takes. All simulated:{' '}
          {recherchiert ? `researched personas ${zielgruppe}` : `personas ${zielgruppe}, without evidence from real sources`}
          {belege.fachpruefer ? ' and an AI reviewer' : ''}, no real visitors.
          </>)}
        </p>
      </div>
      <div className={`grid gap-3 md:gap-5 ${n === 3 ? 'md:grid-cols-3 print:grid-cols-3' : n === 2 ? 'md:grid-cols-2 print:grid-cols-2' : ''}`}>
        {belege.reihe.map(({ art, nr }) => {
          if (art === 'text' && belege.text) {
            const tb = belege.text;
            return (
              <a key={art} href={`#${BELEG_ANKER[art]}`} className={karte}>
                {kopf(nr, art, tb.bestanden)}
                <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                  <b className={gross}>{t(`${tb.dafuer} von ${tb.gesamt}`, `${tb.dafuer} of ${tb.gesamt}`)}</b>
                  <span className="text-[0.84375rem] text-[#565b62]">{tb.art === 'feedback' ? t('Personas gehen weiter', 'personas move on') : t(`${tb.einheit} für neu`, `${tb.einheit} for new`)}</span>
                </span>
                {tb.art === 'vergleich' && <Balkenpaar heute={tb.heute} neu={tb.neu} altLabel={tb.altLabel} />}
                <ZumBeleg nr={nr} />
              </a>
            );
          }
          if (art === 'design' && belege.design) {
            const l = belege.design.lesart;
            const g = l?.groesste;
            const ersterEindruck = belege.design.neu?.noten?.erster_eindruck;
            return (
              <a key={art} href={`#${BELEG_ANKER[art]}`} className={karte}>
                {kopf(nr, art, !!l?.bestanden)}
                {l && g ? (
                  <>
                    <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                      <b className={`${gross} whitespace-nowrap`}>{zahl1(g.heute)} → {zahl1(g.neu)}</b>
                      <span className="text-[0.84375rem] text-[#565b62]">{noteBei(g.kriterium)}</span>
                    </span>
                    <p className="m-0 text-[0.84375rem] leading-normal text-[#2f3238]">{l.satz}.</p>
                  </>
                ) : l ? (
                  // Kein deutlicher Unterschied: keine Pfeilzahl, die die Lesart selbst als Rauschen einstuft.
                  <>
                    <b className={gross}>{t('etwa gleich', 'about the same')}</b>
                    <p className="m-0 text-[0.84375rem] leading-normal text-[#2f3238]">{l.satz}.</p>
                  </>
                ) : (
                  <>
                    <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                      <b className={gross}>{typeof ersterEindruck === 'number' ? zahl1(ersterEindruck) : '–'}</b>
                      <span className="text-[0.84375rem] text-[#565b62]">{t('von 10, erster Eindruck', 'of 10, first impression')}</span>
                    </span>
                    <p className="m-0 text-[0.84375rem] leading-normal text-[#2f3238]">
                      {belege.design.unvergleichbar
                        ? t('Heute und neu hatten verschiedene Jurys, deshalb ohne Vergleich.', 'Current and new had different juries, so there is no comparison.')
                        : t('Die neue Fassung ohne Check der heutigen Seite zum Vergleich.', 'The new version, without a check of the current page to compare.')}
                    </p>
                  </>
                )}
                <ZumBeleg nr={nr} />
              </a>
            );
          }
          if (art === 'fachpruefer' && belege.fachpruefer) {
            const f = belege.fachpruefer;
            return (
              <a key={art} href={`#${BELEG_ANKER[art]}`} className={karte}>
                {kopf(nr, art, f.bestanden)}
                <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                  <b className={gross}>{f.schwer}</b>
                  <span className="text-[0.84375rem] text-[#565b62]">{t('schwere Befunde (Grad 3 oder 4)', 'serious findings (grade 3 or 4)')}</span>
                </span>
                <p className="m-0 text-[0.84375rem] leading-normal text-[#2f3238]">{[f.bereicheSatz, f.grad2Satz].filter(Boolean).join(' ')}</p>
                <ZumBeleg nr={nr} />
              </a>
            );
          }
          return null;
        })}
      </div>
    </section>
  );
}

/**
 * Kopf eines Belegs: Siegel, „Beleg n von m · Art“, Überschrift, Etiketten. Das
 * Siegel trägt hier keinen Haken (wie im Entwurf), den tragen nur die Karten
 * der Übersicht. Am Handy dürfen die Etiketten umbrechen: bei großer
 * Grundschrift liefen sie sonst über den Rand.
 */
function BelegKopf({ nr, von, name, titel, marken, id }: {
  nr: number; von: number; name: string; titel: string; marken: { text: string; ton: 'plus' | 'minus' | 'neutral' }[]; id: string;
}) {
  const marke = (m: { text: string; ton: string }, i: number) => (
    <span key={i} className={`rounded-full px-2.5 py-1 text-[0.75rem] font-semibold md:shrink-0 md:px-[13px] md:py-1.5 md:text-[0.8125rem] md:whitespace-nowrap ${
      m.ton === 'plus' ? 'bg-[#e4ede3] text-[#2f5a34]' : m.ton === 'minus' ? 'bg-[#f6e3d2] text-[#8a4a17]' : 'bg-[#e8eceb] text-[#2f3238]'}`}>{m.text}</span>
  );
  return (
    <div className="flex flex-col gap-2.5 border-b border-[#dde3e1] pb-3 break-inside-avoid break-after-avoid md:flex-row md:items-center md:gap-5 md:pb-4 print:flex-row print:items-center print:gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2.5 md:contents print:contents">
        <span className="flex items-center gap-2.5 md:contents print:contents">
          <Siegel nr={nr} haken={false} klein />
          <span className={`${KICK} md:hidden print:hidden`}>{t(`Beleg ${nr} von ${von}`, `Evidence ${nr} of ${von}`)}<br />{name}</span>
        </span>
        {marken.length > 0 && <span className="flex min-w-0 flex-wrap justify-end gap-1.5 md:hidden print:hidden">{marken.slice(0, 1).map(marke)}</span>}
      </div>
      <div className="flex min-w-0 grow flex-col gap-1">
        <span className={`${KICK} hidden md:block print:block`}>{t(`Beleg ${nr} von ${von}`, `Evidence ${nr} of ${von}`)} · {name}</span>
        <h2 id={id} className={`${KOPF} m-0 text-[1.5rem] leading-[1.15] md:text-[2rem]`}>{titel}</h2>
      </div>
      {marken.length > 0 && <span className="hidden shrink-0 gap-2 md:flex print:flex">{marken.map(marke)}</span>}
    </div>
  );
}

const ABSATZ = 'm-0 max-w-[920px] text-[0.875rem] leading-relaxed text-[#2f3238] md:text-[0.9375rem]';
const ZITAT = `${SERIF} m-0 text-[1rem] leading-[1.42] text-[#2f3238] italic md:text-[1.0625rem]`;
const WER = "mt-1 block text-[0.75rem] text-[#5f646b] not-italic font-sans md:text-[0.78125rem]";

/** Beleg 1: die Text-Simulation, ausführlich. */
function BelegText({ t: b, nr, von, data, kurz }: { t: TextBeleg; nr: number; von: number; data: any; kurz: (l: string) => string }) {
  const stand = standDer(data);
  const kr = kernRandZahl(data);
  const wirkt = wasWirkt(data);
  const stimmen = stimmenNeu(data);
  const heute = stimmeHeute(data);
  const stoert = stoertNoch(data);
  const kernRand = leseKernRand(stand);
  const muster = stand?.mode === 'feedback' ? null : leseMuster(stand);
  const bilanz = leseBilanz(stand?.versprechen);
  const kollisionen = kollisionenDesBerichts(Array.isArray(data?.variants) ? data.variants : []);
  const u = data?.umfang;
  const runden = typeof u?.runden === 'number' ? u.runden : 0;
  const nurLetzte = u?.gezeigt?.nur_letzte === true && runden > 1;
  const gegen = b.istHeute
    ? t('die heutige Seite und die neue Fassung', 'the current page and the new version')
    : t(`die neue Fassung und ${b.gegen}`, `the new version and ${b.gegen}`);
  // Nicht „je Kaufanlass einmal“: in einer rotierten Matrix liest nicht jede
  // Persona unter jedem Anlass, und bei Privatkunden oder in der Politik passt
  // „Kaufanlass“ nicht.
  const urteileSatz = b.urteile > b.personas && b.personas > 0
    ? t(` ${b.urteile} Urteile: jede Persona urteilt mehrmals, unter verschiedenen Anlässen.`, ` ${b.urteile} verdicts: each persona judges several times, in different situations.`)
    : '';
  const kernSatz = kr && kr.kern + kr.rand === (b.personas || kr.kern + kr.rand)
    ? t(` (${kr.kern} aus der Kernzielgruppe, ${kr.rand} aus der erweiterten Zielgruppe)`, ` (${kr.kern} from the core audience, ${kr.rand} from the extended audience)`)
    : '';
  // Die Warnung „überzeugt vor allem die erweiterte Zielgruppe“ aus den Zahlen; der
  // eingefrorene Satz des Servers nur, wenn sie den Fall nicht hergeben.
  const warnung = kernRand
    ? (randWarnung(kernRand, { vergleich: vergleichFuerSatz(data, stand), urteile: b.urteile > b.personas }) ?? kernRand.warnung)
    : null;
  // „Kern-Urteilen“ (nach „von“) wird „Kern-Urteile“; Personas bleiben Personas.
  const einheitKurz = sprache() === 'en' ? b.einheit : b.einheit.replace(/en$/, 'e');
  const jederPunkt = sprache() === 'en'
    ? (b.matrix ? (b.kern ? 'one core verdict' : 'one verdict') : (b.kern ? 'one core persona' : 'one persona'))
    : b.einheit.includes('Urteil')
      ? (b.einheit.startsWith('Kern') ? 'ein Kern-Urteil' : 'ein Urteil')
      : (b.einheit.startsWith('Kern') ? 'eine Kern-Persona' : 'eine Persona');
  const personasZahl = b.personas || (Array.isArray(data?.set?.personas) ? data.set.personas.length : 0);
  const punkte = b.art === 'vergleich' && b.gesamt > 0 && b.gesamt <= 80;
  // Zwei Spalten wie im Entwurf: Noten links, „Was wirkt“ rechts. Eine Spalte
  // „Nächster Schritt“ gibt es in keinem der abgenommenen Entwürfe.
  const spalten = 'lg:grid-cols-[330px_minmax(0,1fr)]';
  const pro = (g: { n: number; dafuer: number; heute: number | null; neu: number | null }) =>
    `${g.dafuer} ${t('für neu', 'for new')}${g.heute != null && g.neu != null ? ` · ${zahl1(g.heute)} → ${zahl1(g.neu)}` : ''}`;
  const einheitGruppe = b.urteile > b.personas ? t('Urteile', 'verdicts') : t('Personas', 'personas');

  return (
    <section id="beleg-text" aria-labelledby="beleg-text-titel" className="flex scroll-mt-6 flex-col gap-[18px] md:gap-[26px]">
      <BelegKopf nr={nr} von={von} name={belegName('text')} titel={b.titel} id="beleg-text-titel"
        marken={[{ text: b.etikett, ton: b.bestanden ? 'plus' : b.dagegen ? 'minus' : 'neutral' }]} />
      <p className={ABSATZ}>
        {t(
          <>{personasZahl} Personas{data.set?.name ? <> aus der Zielgruppe „{data.set.name}“</> : null}{kernSatz} lesen {gegen}, ohne zu wissen, welche neu ist.</>,
          <>{personasZahl} {personasZahl === 1 ? 'persona' : 'personas'}{data.set?.name ? <> from the audience “{data.set.name}”</> : null}{kernSatz} read {gegen} without knowing which one is new.</>,
        )}{urteileSatz}{kernSatz && <> {kernRandText()}</>}
        {nurLetzte && t(
          <> Das ist die letzte von {runden} Runden. Die übrigen stehen unten unter <a className="text-[#1f6964] underline underline-offset-2" href="#akkordeon-methode">Methode und Umfang</a>.</>,
          <> This is the last of {runden} rounds. The others are below under <a className="text-[#1f6964] underline underline-offset-2" href="#akkordeon-methode">Method and scope</a>.</>,
        )}
      </p>

      <div className={`grid items-start gap-6 lg:gap-9 ${spalten}`}>
        {b.art === 'vergleich' && (
          <div className="flex flex-col gap-3.5 rounded-[14px] border border-[#dde3e1] bg-[#ffffff] p-4 break-inside-avoid md:gap-4 md:p-[22px]">
            <span className={KICK}>{t(`Note${kernRand ? ' im Kern' : ''}, 0 bis 10`, `Score${kernRand ? ' in the core' : ''}, 0 to 10`)}</span>
            <Balkenpaar heute={b.heute} neu={b.neu} altLabel={b.altLabel} dick />
            {punkte && (
              <div className="flex flex-col gap-2 pt-1">
                <div className="flex flex-wrap gap-1 md:gap-[5px]" role="img"
                  aria-label={t(
                    `${b.gesamt} ${einheitKurz}: ${b.dafuer} für die neue Fassung, ${b.gesamt - b.dafuer} für ${b.istHeute ? 'die heutige' : 'die Vergleichsfassung'}`,
                    `${b.gesamt} ${einheitKurz}: ${b.dafuer} for the new version, ${b.gesamt - b.dafuer} for ${b.istHeute ? 'the current one' : 'the baseline'}`,
                  )}>
                  {Array.from({ length: b.gesamt }, (_, i) => (
                    <span key={i} className={`size-[11px] rounded-full ${i < b.dafuer ? 'bg-[#3f8a87]' : 'bg-[#aeb7b5]'}`} />
                  ))}
                </div>
                <span className="text-[0.75rem] leading-snug text-[#5f646b] md:text-[0.78125rem]">
                  {t(`Jeder Punkt ${jederPunkt}: ${b.dafuer} für neu, ${b.gesamt - b.dafuer} für ${b.altLabel}.`,
                    `Each dot is ${jederPunkt}: ${b.dafuer} for new, ${b.gesamt - b.dafuer} for ${b.altLabel}.`)}
                </span>
              </div>
            )}
            {(b.alle || b.rand) && (
              <div className="flex flex-col gap-1.5 border-t border-[#dde3e1] pt-3 text-[0.8125rem] leading-snug md:text-[0.84375rem]">
                {b.alle && <div className="flex justify-between gap-2.5"><span className="text-[#565b62]">{t('Alle', 'All')} {b.alle.n} {einheitGruppe}</span><b className="text-right font-semibold">{pro(b.alle)}</b></div>}
                {b.rand && <div className="flex justify-between gap-2.5"><span className="text-[#565b62]">{t('Erweiterte Zielgruppe', 'Extended audience')}, {b.rand.n} {einheitGruppe}</span><b className="text-right font-semibold">{pro(b.rand)}</b></div>}
              </div>
            )}
          </div>
        )}

        {(wirkt.length > 0 || stimmen.length > 0) && (
          <div className="flex flex-col gap-3 pt-1 md:gap-4">
            <span className={KICK}>{t('Was wirkt', 'What works')}</span>
            {wirkt.length > 0 && (
              <div className="flex flex-col gap-2.5">
                {wirkt.map((w) => (
                  <div key={w.abschnitt} className="grid grid-cols-[96px_minmax(0,1fr)] items-baseline gap-2.5 md:grid-cols-[116px_minmax(0,1fr)] md:gap-3">
                    <b className={`${SERIF} text-[1.375rem] font-semibold whitespace-nowrap md:text-[1.625rem]`}>{t(`${w.n} von ${w.von}`, `${w.n} of ${w.von}`)}</b>
                    <span className="text-[0.84375rem] leading-snug text-[#2f3238] md:text-[0.875rem]">{t(
                      <>{w.kern ? 'Kern-Personas' : 'Personas'} nennen „{w.abschnitt}“ als Stärke</>,
                      <>{w.kern ? 'Core personas' : 'Personas'} name “{w.abschnitt}” as a strength</>,
                    )}</span>
                  </div>
                ))}
              </div>
            )}
            {stimmen.length > 0 && (
              <div className={`flex flex-col gap-3.5 ${wirkt.length ? 'border-t border-[#dde3e1] pt-3.5' : ''}`}>
                {stimmen.map((s, i) => (
                  <blockquote key={i} className={ZITAT}>{zitiert(s.text)}{s.persona && <span className={WER}>{kurz(s.persona)}</span>}</blockquote>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {(heute || stoert.length > 0) && (
        <div className={`grid gap-6 border-t border-[#dde3e1] pt-5 md:pt-[22px] lg:gap-9 ${heute && stoert.length ? 'lg:grid-cols-[340px_minmax(0,1fr)]' : ''}`}>
          {heute && (
            <div className="flex flex-col gap-2.5">
              <span className={KICK}>{t('Über die heutige Seite', 'About the current page')}</span>
              <blockquote className={ZITAT}>{zitiert(heute.text)}{heute.persona && <span className={WER}>{kurz(heute.persona)}</span>}</blockquote>
            </div>
          )}
          {stoert.length > 0 && (
            <div className="flex flex-col gap-3 rounded-xl bg-[#eef2f1] p-3.5 md:bg-transparent md:p-0 print:bg-transparent print:p-0">
              <span className={KICK}>{t('Was an der neuen Fassung noch stört', 'What still bothers about the new version')}</span>
              {stoert.map((s, i) => (
                <div key={i} className="grid gap-1.5 md:grid-cols-[minmax(0,1fr)_200px_64px] md:items-center md:gap-3.5">
                  <span className="text-[0.8125rem] leading-snug text-[#2f3238] md:text-[0.875rem]">{zitiert(s.einwand)}</span>
                  <span className="flex items-center gap-2 md:contents">
                    <span className="h-[7px] min-w-0 flex-1 rounded bg-[#dbe1df] md:h-2 md:bg-[#e8eceb]" aria-hidden>
                      <span className="block h-full rounded bg-[#3f8a87]" style={{ width: `${s.von ? Math.min(100, (s.n / s.von) * 100) : 0}%` }} />
                    </span>
                    <b className="w-11 shrink-0 text-right text-[0.78125rem] tabular-nums md:w-auto md:text-[0.875rem]">{s.n} / {s.von}</b>
                  </span>
                </div>
              ))}
              <span className="text-[0.75rem] text-[#5f646b] md:text-[0.78125rem]">
                {t(`${stoert[0]!.kern ? 'Kern-Personas' : 'Personas'} von ${stoert[0]!.von}, die es nennen.`,
                  `${stoert[0]!.kern ? 'Core personas' : 'Personas'} who mention it, out of ${stoert[0]!.von}.`)}
              </span>
              {/* Eine eigene Zeile mit 44 px Tippfläche: der Link steht nicht mitten im Satz. */}
              <a href="#akkordeon-einwaende" className={`inline-flex min-h-11 items-center self-start text-[0.8125rem] font-semibold text-[#1f6964] underline-offset-2 hover:underline print:hidden ${FOKUS}`}>{t('Alle Einwände', 'All objections')}</a>
            </div>
          )}
        </div>
      )}

      {(warnung || muster || bilanz || kollisionen.length > 0) && (
        <div className="flex max-w-[62ch] flex-col gap-2">
          {warnung && <p className="m-0 rounded-md border border-[#cbd3d1] bg-[#eef2f1] px-3.5 py-2.5 text-[0.875rem] leading-relaxed text-[#2f3238]">{warnung}</p>}
          {muster && <p className="m-0 rounded-md border border-[#cbd3d1] bg-[#eef2f1] px-3.5 py-2.5 text-[0.875rem] leading-relaxed text-[#2f3238]">{musterSatz(muster)}</p>}
          {bilanz && <p className="m-0 text-[0.875rem] text-[#5f646b]">{t('Markenversprechen: ', 'Brand promise: ')}{bilanzSatz(bilanz)}{t(' Zählt nicht in den Score.', ' Does not count toward the score.')}</p>}
          {kollisionen.length > 0 && (
            <p className="m-0 rounded-lg border border-[#e0a46e] bg-[#fdf3ea] px-4 py-3 text-[0.9375rem] leading-relaxed text-[#1d1f26]">
              <b>{t('Kollidiert mit euren Leitplanken:', 'Conflicts with your guardrails:')}</b> {leitplankenTitel(kollisionen)} {t('Wir empfehlen das nicht, ihr entscheidet.', 'We do not recommend it, you decide.')}{' '}
              <a href="#akkordeon-leitplanken" className="font-semibold whitespace-nowrap text-[#1f6964] underline-offset-[3px] hover:underline print:hidden">{t('Zur Liste', 'To the list')}</a>
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Eine Zeile der Design-Tabelle: Skala 0 bis 10 mit Strich bei 5, heute als
 * Ring, neu als Punkt. Farbig hinterlegt nur, was deutlich ist (ab dem
 * Doppelten der Rauschgrenze); eine Tendenz steht farbig, aber ohne Fläche.
 */
function SkalaZeile({ z, grenze }: { z: DesignLesart['zeilen'][number]; grenze: number }) {
  const klar = z.richtung === 'deutlich-besser' || z.richtung === 'deutlich-schlechter';
  const tendenz = z.richtung === 'besser' || z.richtung === 'schlechter';
  const plus = z.abstand > 0;
  const lo = Math.min(z.heute, z.neu);
  const hintergrund = klar ? (plus ? 'bg-[#e4ede3]' : 'bg-[#f6e3d2]') : 'border-t border-[#e8eceb]';
  const farbe = klar || tendenz ? (plus ? 'text-[#2f5a34]' : 'text-[#8a4a17]') : 'text-[#2f3238]';
  const titel = sprache() === 'en'
    ? (klar ? `clear: from ${zahl1(2 * grenze)}`
      : tendenz ? `tendency: above the noise limit of ${zahl1(grenze)}, below ${zahl1(2 * grenze)}` : `in the noise (up to ${zahl1(grenze)})`)
    : klar ? `deutlich: ab ${zahl1(2 * grenze)}`
      : tendenz ? `Tendenz: über der Rauschgrenze von ${zahl1(grenze)}, unter ${zahl1(2 * grenze)}` : `im Rauschen (bis ${zahl1(grenze)})`;
  const skala = (
    <div className="relative h-[22px] md:h-7" aria-hidden>
      <div className="absolute inset-x-0 top-[10px] h-0.5 bg-[#dde3e1] md:top-[13px]" />
      <div className="absolute top-[5px] left-1/2 h-3 w-px bg-[#c3cbc9] md:top-[7px] md:h-3.5" />
      <div className="absolute top-[9px] h-1 rounded-sm md:top-3" style={{ left: `${lo * 10}%`, width: `${Math.abs(z.neu - z.heute) * 10}%`, background: plus ? '#8cc5c1' : '#c3cbc9' }} />
      <div className="absolute top-1 size-3.5 rounded-full border-[3px] border-[#7d8487] bg-[#ffffff] md:top-[7px]" style={{ left: `calc(${z.heute * 10}% - 7px)` }} />
      <div className="absolute top-1 size-3.5 rounded-full bg-[#3f8a87] md:top-[7px]" style={{ left: `calc(${z.neu * 10}% - 7px)` }} />
    </div>
  );
  return (
    <div className={`rounded-[10px] px-2.5 py-2 md:grid md:grid-cols-[140px_minmax(0,1fr)_90px_52px] md:items-center md:gap-3.5 md:px-3 md:py-2.5 print:grid print:grid-cols-[30mm_minmax(0,1fr)_22mm_14mm] print:items-center print:gap-3 ${hintergrund}`}
      title={titel}>
      <div className="flex items-baseline justify-between gap-2 md:contents print:contents">
        <span className={`text-[0.875rem] ${klar ? `font-bold ${farbe}` : tendenz ? `font-semibold ${farbe}` : 'text-[#2f3238]'}`}>
          {z.label}{tendenz && <span className="font-normal text-[#5f646b]">{t(' (Tendenz)', ' (tendency)')}</span>}
        </span>
        <span className="text-[0.8125rem] text-[#2f3238] tabular-nums md:hidden print:hidden">
          {zahl1(z.heute)} → {zahl1(z.neu)} <b className={klar ? farbe : tendenz ? `font-semibold ${farbe}` : 'font-medium text-[#5f646b]'}>{vorzeichen(z.abstand)}</b>
        </span>
      </div>
      {skala}
      <span className="hidden text-right text-[0.875rem] tabular-nums md:block print:block">{zahl1(z.heute)} → {zahl1(z.neu)}</span>
      <b className={`hidden text-right text-[0.875rem] tabular-nums md:block print:block ${klar ? `font-bold ${farbe}` : tendenz ? `font-semibold ${farbe}` : 'font-medium text-[#5f646b]'}`}>{vorzeichen(z.abstand)}</b>
    </div>
  );
}

/** Beleg 2: die Design-Simulation, mit ehrlicher Lesart. */
function BelegDesign({ d, nr, von, skizze }: { d: NonNullable<Belege['design']>; nr: number; von: number; skizze: boolean }) {
  const l = d.lesart;
  const { heute, neu } = d;
  const pz = (n: number) => t(`${n} Personas`, `${n} ${n === 1 ? 'persona' : 'personas'}`);
  const personas = l ? (l.personas.heute && l.personas.neu
    ? (l.personas.heute === l.personas.neu ? pz(l.personas.heute) : t(`${l.personas.heute} und ${l.personas.neu} Personas`, `${l.personas.heute} and ${l.personas.neu} personas`))
    : t('Die Personas', 'The personas')) : (typeof neu?.personas === 'number' ? pz(neu.personas) : t('Die Personas', 'The personas'));
  const ansicht: 'mobil' | 'desktop' = l?.groesste?.kriterium === 'handy' ? 'mobil' : 'desktop';
  const bild = (x: any) => {
    const s = x?.bild_links?.[ansicht];
    return typeof s === 'string' && /^https?:\/\//i.test(s) ? s : null;
  };
  const note = (x: any) => (l?.groesste ? x?.noten?.[l.groesste.kriterium] : null);
  const mitBildern = !!(l && bild(heute) && bild(neu));
  const wo = ansicht === 'mobil' ? t('am Handy', 'on mobile') : t('am Desktop', 'on desktop');
  // Beide Checks vom selben Tag: einmal „beide am …“ statt zweimal dasselbe Datum.
  const tH = tag(heute?.am);
  const tN = tag(neu?.am);
  const wann = tH && tH === tN
    ? t(`beide am ${tH}`, `both on ${tH}`)
    : t(`heute am ${tH || 'unbekannt'}, neu am ${tN || 'unbekannt'}`, `current on ${tH || 'unknown'}, new on ${tN || 'unknown'}`);

  return (
    <section id="beleg-design" aria-labelledby="beleg-design-titel" className="flex scroll-mt-6 flex-col gap-[18px] md:gap-[26px]">
      <BelegKopf nr={nr} von={von} name={belegName('design')} id="beleg-design-titel"
        titel={l ? l.satz : t('Wie die neue Fassung wirkt', 'How the new version comes across')} marken={l ? l.marken : []} />
      <p className={ABSATZ}>
        {t(`${personas} scrollen die Seite am Desktop und am Handy durch und geben Noten von 0 bis 10, 5 ist neutral.`,
          `${personas} scroll through the page on desktop and mobile and give scores from 0 to 10, 5 is neutral.`)}
        {l
          ? t(<> Heute und neu sind zwei getrennte Checks ({wann}). Das ist kein direkter A/B-Vergleich, die beiden Fassungen lagen nie nebeneinander.</>,
            <> Current and new are two separate checks ({wann}). This is not a direct A/B comparison, the two versions were never side by side.</>)
          : d.unvergleichbar ? <> {d.unvergleichbar}</>
            : t(<> Ein Check der heutigen Seite liegt diesem Bericht nicht bei, die Noten stehen deshalb ohne Vergleich.</>,
              <> This report has no check of the current page, so the scores stand without a comparison.</>)}
        {d.jury && <> {d.jury}</>}
      </p>
      {skizze && <SkizzeZeile />}

      {l ? (
        <div className={`grid items-start gap-6 lg:gap-12 ${mitBildern ? 'lg:grid-cols-[minmax(0,1fr)_452px]' : ''}`}>
          <div className="flex flex-col gap-1 md:gap-1.5">
            <div className="hidden grid-cols-[140px_minmax(0,1fr)_90px_52px] gap-3.5 px-3 pb-1.5 text-[0.75rem] text-[#5f646b] md:grid print:grid print:grid-cols-[30mm_minmax(0,1fr)_22mm_14mm] print:gap-3">
              <span>{t('Note', 'Score')}</span>
              <span className="flex justify-between"><span>0</span><span>{t('5 neutral', '5 neutral')}</span><span>10</span></span>
              <span className="text-right">{t('heute → neu', 'current → new')}</span>
              <span className="text-right">{t('Abstand', 'Gap')}</span>
            </div>
            {l.zeilen.map((z) => <SkalaZeile key={z.kriterium} z={z} grenze={l.grenze} />)}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-2.5 pt-2 text-[0.75rem] text-[#565b62] md:px-3 md:pt-2.5 md:text-[0.78125rem]">
              <span className="flex items-center gap-1.5"><span className="size-3 rounded-full border-[3px] border-[#7d8487] bg-[#ffffff]" aria-hidden />{t('heute', 'current')}</span>
              <span className="flex items-center gap-1.5"><span className="size-3 rounded-full bg-[#3f8a87]" aria-hidden />{t('neu', 'new')}</span>
              <span className="md:hidden">{t('Skala 0 bis 10, Strich bei 5', 'Scale 0 to 10, mark at 5')}</span>
            </div>
            <p className="mt-3 mb-0 rounded-xl bg-[#eef2f1] px-3.5 py-3 text-[0.84375rem] leading-relaxed text-[#2f3238] md:px-4 md:py-3.5 md:text-[0.875rem]">{l.erklaerung}</p>
          </div>
          {mitBildern && (
            <div className={`grid gap-3 break-inside-avoid md:gap-4 print:max-w-[110mm] ${ansicht === 'mobil' ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {([['heute', heute], ['neu', neu]] as const).map(([rolle, x]) => (
                <figure key={rolle} className="m-0 flex min-w-0 flex-col gap-1.5 md:gap-2">
                  <span className="flex items-baseline justify-between gap-1.5">
                    <span className={`text-[0.78125rem] font-semibold md:text-[0.8125rem] ${rolle === 'neu' ? 'text-[#2f5a34]' : 'text-[#2f3238]'}`}>{rolle === 'heute' ? t('Heute', 'Current') : t('Neu', 'New')} {wo}</span>
                    {typeof note(x) === 'number' && <b className={`${SERIF} text-[1.25rem] font-semibold md:text-[1.375rem] ${rolle === 'neu' ? 'text-[#2f5a34]' : 'text-[#565b62]'}`}>{zahl1(note(x))}</b>}
                  </span>
                  <a href={bild(x)!} target="_blank" rel="noopener noreferrer" className={`block rounded-xl ${FOKUS}`} title={t('Ganze Aufnahme öffnen', 'Open full capture')}>
                    <img src={bild(x)!} alt={t(`${rolle === 'heute' ? 'Heutige Seite' : 'Neue Fassung'} ${wo}, erster Bildschirm`, `${rolle === 'heute' ? 'Current page' : 'New version'} ${wo}, first screen`)}
                      className={`block w-full rounded-xl border border-[#d9e0de] bg-white object-cover object-top ${ansicht === 'mobil' ? 'aspect-[340/720]' : 'aspect-[1280/800]'}`} />
                  </a>
                </figure>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex max-w-[560px] flex-col gap-2">
          {Object.entries(kriteriumKurz()).filter(([k]) => typeof neu?.noten?.[k] === 'number').map(([k, label]) => (
            <div key={k} className="grid grid-cols-[minmax(0,9rem)_1fr_2.5rem] items-center gap-3 text-[0.8125rem]">
              <span className={neu?.schwaechste === k ? 'font-semibold text-[#1d1f26]' : 'text-[#5f646b]'}>{label}{neu?.schwaechste === k ? t(' (schwächste)', ' (weakest)') : ''}</span>
              <span className="h-2 overflow-hidden rounded-full bg-[#e3e8e6]" aria-hidden><span className="block h-full rounded-full bg-[#3f8a87]" style={{ width: `${Math.min(100, neu.noten[k] * 10)}%` }} /></span>
              <span className="text-right tabular-nums">{zahl1(neu.noten[k])}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const GRAD_ZEILEN: { grad: 4 | 3 | 2; text: string; en: string }[] = [
  { grad: 4, text: 'verhindert die Anfrage', en: 'prevents the inquiry' },
  { grad: 3, text: 'kostet spürbar', en: 'costs noticeably' },
  { grad: 2, text: 'stört', en: 'gets in the way' },
];

/** Beleg 3: der Fachprüfer der neuen Fassung, als KI-Prüfer gekennzeichnet. */
function BelegFachpruefer({ f, nr, von, heute, skizze }: { f: FachprueferBeleg; nr: number; von: number; heute: FachprueferBeleg | null; skizze: boolean }) {
  const kachel = (marker: string) => (marker === 'gut' ? 'bg-[#e4ede3] text-[#2f5a34]'
    : marker === 'teilweise' ? 'bg-[#f6e3d2] text-[#8a4a17]'
      : marker === 'fehlt' ? 'bg-[#f3d9c8] text-[#7a3510]' : 'bg-[#e8eceb] text-[#565b62]');
  const spalten = f.bereiche.length ? 'lg:grid-cols-[360px_360px_minmax(0,1fr)]' : 'lg:grid-cols-[360px_minmax(0,1fr)]';
  return (
    <section id="beleg-fachpruefer" aria-labelledby="beleg-fachpruefer-titel" className="flex scroll-mt-6 flex-col gap-[18px] md:gap-[26px]">
      <BelegKopf nr={nr} von={von} name={t('Fachprüfer (KI)', 'Expert reviewer (AI)')} id="beleg-fachpruefer-titel" titel={f.titel}
        marken={[{ text: f.etikett, ton: f.bestanden ? 'plus' : 'minus' }]} />
      <p className={ABSATZ}>
        {t('Der Fachprüfer ist ein KI-Prüfer, kein Mensch. Er geht die neue Seite wie ein Designer nach festem Raster durch und belegt jeden '
          + 'Befund mit dem Bildschirm. Grad 4 verhindert die Anfrage, Grad 3 kostet spürbar, Grad 2 stört, Grad 0 bis 1 ist Feinschliff.',
          'The expert reviewer is an AI reviewer, not a person. It goes through the new page like a designer, using a fixed grid, and backs every '
          + 'finding with the screen. Grade 4 prevents the inquiry, grade 3 costs noticeably, grade 2 gets in the way, grade 0 to 1 is polish.')}
      </p>
      {skizze && <SkizzeZeile pruefer />}

      <div className={`grid items-start gap-6 lg:gap-9 ${spalten}`}>
        {f.bereiche.length > 0 && (
          <div className="flex flex-col gap-2.5 md:gap-3.5">
            <span className={KICK}>{Wort(f.bereiche.length)} {t('Bereiche', f.bereiche.length === 1 ? 'area' : 'areas')}</span>
            <ul className="m-0 grid list-none grid-cols-2 gap-1.5 p-0 md:grid-cols-3 md:gap-2">
              {f.bereiche.map((b) => (
                <li key={b.bereich} className={`flex flex-col gap-0.5 rounded-[10px] px-3 py-2.5 ${kachel(b.marker)}`}>
                  <span className="text-[0.8125rem] font-semibold md:text-[0.84375rem]">{b.label}</span>
                  <span className="text-[0.71875rem] font-medium md:text-[0.75rem]">{markerText()[b.marker] ?? b.marker}</span>
                </li>
              ))}
            </ul>
            <p className="m-0 rounded-[10px] border border-[#dde3e1] bg-[#ffffff] px-3.5 py-3 text-[0.8125rem] leading-snug text-[#2f3238] md:text-[0.84375rem]">
              {f.gemessen
                ? t(<><b className="font-semibold">Gemessen, nicht geschätzt:</b> Kontrast, Schriftgrößen und Tippflächen nimmt der Prüfer aus einer Messung der Seite.</>,
                  <><b className="font-semibold">Measured, not estimated:</b> The reviewer takes contrast, font sizes and tap targets from a measurement of the page.</>)
                : t(<><b className="font-semibold">Ohne Messung:</b> Diese Fassung wurde nicht vermessen, bei einem Entwurf oder einer Skizze ist das so. Befunde, die eine Messung bräuchten (Kontrast, Tippflächen), fehlen deshalb.</>,
                  <><b className="font-semibold">Without measurement:</b> This version was not measured, which is how it is for a draft or a sketch. Findings that would need a measurement (contrast, tap targets) are therefore missing.</>)}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2 md:gap-3.5">
          <span className={KICK}>{t('Befunde nach Grad', 'Findings by grade')}</span>
          <div className="flex flex-col">
            {GRAD_ZEILEN.map((g) => (
              <div key={g.grad} className="grid grid-cols-[88px_minmax(0,1fr)_40px] items-center gap-2 border-t border-[#dde3e1] py-2 md:grid-cols-[96px_minmax(0,1fr)_54px] md:gap-3 md:py-2.5">
                <b className="text-[0.8125rem] font-semibold md:text-[0.84375rem]">{t('Grad', 'Grade')} {g.grad}</b>
                <span className="text-[0.78125rem] leading-snug text-[#565b62] md:text-[0.84375rem]">{t(g.text, g.en)}</span>
                <b className={`${SERIF} text-right text-[1.75rem] leading-none font-semibold ${f.grad[g.grad] === 0 ? 'text-[#2f5a34]' : 'text-[#8a4a17]'}`}>{f.grad[g.grad]}</b>
              </div>
            ))}
            <div className="grid grid-cols-[88px_minmax(0,1fr)_40px] items-center gap-2 border-t border-[#dde3e1] py-2 md:grid-cols-[96px_minmax(0,1fr)_54px] md:gap-3 md:py-2.5">
              <b className="text-[0.8125rem] font-semibold md:text-[0.84375rem]">{t('Grad 0 bis 1', 'Grade 0 to 1')}</b>
              <span className="text-[0.78125rem] leading-snug text-[#565b62] md:text-[0.84375rem]">{t('weitere, nur Feinschliff', 'others, polish only')}</span>
              <b className={`${SERIF} text-right text-[1.0625rem] leading-none font-semibold text-[#5f646b]`}>{f.kleinere}</b>
            </div>
          </div>
          {heute && (
            <span className="text-[0.75rem] leading-snug text-[#5f646b] md:text-[0.78125rem]">
              {sprache() === 'en'
                ? `For comparison, the current page: ${heute.schwer === 0 ? 'no finding' : heute.schwer === 1 ? 'one finding' : `${heute.schwer} findings`} with grade 3 or 4.`
                : `Zum Vergleich, die heutige Seite: ${heute.schwer === 0 ? 'kein Befund' : heute.schwer === 1 ? 'ein Befund' : `${heute.schwer} Befunde`} mit Grad 3 oder 4.`}
              {/* Gemessen gegen ungemessen: Befunde mit Messwert fallen beim Entwurf weg, die Zahlen sind nicht gleich gewonnen. */}
              {heute.gemessen !== f.gemessen && t(
                ` Die heutige Seite wurde ${heute.gemessen ? 'mit' : 'ohne'} Messung geprüft, die neue Fassung ${f.gemessen ? 'mit' : 'ohne'}; die Zahlen sind deshalb nicht ganz gleich gewonnen.`,
                ` The current page was checked ${heute.gemessen ? 'with' : 'without'} measurement, the new version ${f.gemessen ? 'with' : 'without'}; the numbers were therefore not obtained in quite the same way.`,
              )}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2 md:gap-3.5">
          {f.befundeSchwer.length > 0 && (
            <>
              <span className={KICK}>{f.befundeSchwer.length === 1 ? t('Der Befund mit Grad 3 oder 4', 'The finding with grade 3 or 4') : t(`Die ${wort(f.befundeSchwer.length)} Befunde mit Grad 3 oder 4`, `The ${wort(f.befundeSchwer.length)} findings with grade 3 or 4`)}</span>
              <ol className="m-0 flex list-decimal flex-col gap-2 pl-5 text-[0.84375rem] leading-normal text-[#2f3238] md:text-[0.875rem]">
                {f.befundeSchwer.map((b, i) => <li key={i}><b className="font-semibold">{t('Grad', 'Grade')} {b.schwere}:</b> {b.was}{b.tun ? <span className="block text-[#565b62]">{t('Tun: ', 'To do: ')}{b.tun}</span> : null}</li>)}
              </ol>
            </>
          )}
          {f.befundeGrad2.length > 0 && (
            <>
              <span className={KICK}>{f.befundeGrad2.length === 1 ? t('Der Befund mit Grad 2', 'The finding with grade 2') : t(`Die ${wort(f.befundeGrad2.length)} Befunde mit Grad 2`, `The ${wort(f.befundeGrad2.length)} findings with grade 2`)}</span>
              <ol className="m-0 flex list-decimal flex-col gap-1.5 pl-[18px] text-[0.84375rem] leading-normal text-[#2f3238] md:gap-2 md:pl-5 md:text-[0.875rem]">
                {f.befundeGrad2.map((b, i) => <li key={i}>{b.was}</li>)}
              </ol>
            </>
          )}
          {f.kleinere > 0 && <span className="text-[0.75rem] text-[#5f646b] md:text-[0.78125rem]">{t('Alle weiteren Befunde sind Feinschliff (Grad 0 bis 1).', 'All other findings are polish (grade 0 to 1).')}</span>}
          {!f.befundeSchwer.length && !f.befundeGrad2.length && <span className="text-[0.84375rem] text-[#565b62]">{t('Kein Befund ab Grad 2.', 'No finding of grade 2 or higher.')}</span>}
        </div>
      </div>

      {f.staerken.length > 0 && (
        <div className="flex flex-col gap-3 border-t border-[#dde3e1] pt-5 md:gap-3.5 md:pt-[22px]">
          <span className={KICK}>{t('Was der Fachprüfer hervorhebt', 'What the expert reviewer highlights')}</span>
          <div className="grid gap-3 md:grid-cols-3 md:gap-8 print:grid-cols-3">
            {f.staerken.map((s, i) => (
              <blockquote key={i} className={`${SERIF} m-0 border-l-[3px] border-[#5e8a62] pl-3 text-[0.96875rem] leading-[1.45] text-[#2f3238] italic md:pl-4 md:text-[1.03125rem]`}>{zitiert(s)}</blockquote>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

